import Alpine from 'alpinejs'
import morph from '@alpinejs/morph'
import ajax from '@imacrayon/alpine-ajax'
import Clipboard from "@ryangjchandler/alpine-clipboard"
import * as Utility from './utility.js';
import alertCreatePage from './components/alertCreatePage.js';
import alertCard from './components/alertCard.js';
import settingsPage from './components/settingsPage.js';
import testPage from './components/testPage.js';
import browserCard from './components/browserCard.js';
import themeModeInput from './components/themeModeInput.js';
import documentationPage from './components/documentationPage.js';
import historyPage from './components/historyPage.js';
import subscriptionsPage from './components/subscriptionsPage.js';

window.Alpine = Alpine
window.Utility = Utility;

Alpine.plugin(Clipboard)
Alpine.plugin(morph)
Alpine.plugin(ajax)

Alpine.data('alertCreatePage', alertCreatePage);
Alpine.data('alertCard', alertCard);
Alpine.data('settingsPage', settingsPage);
Alpine.data('testPage', testPage);
Alpine.data('browserCard', browserCard);
Alpine.data('themeModeInput', themeModeInput);
Alpine.data('documentationPage', documentationPage);
Alpine.data('historyPage', historyPage);
Alpine.data('subscriptionsPage', subscriptionsPage);

Alpine.data('registrationForm', () => ({
    submitting: false,
    errors: {},
    async submit() {
        this.submitting = true;
        this.errors = {};
        try {
            const fd = new FormData(this.$el.querySelector('form'));
            const r = await fetch('/registration', {
                method: 'POST',
                body: fd,
                headers: {'X-Alpine-Request': 'true'},
            });
            const redirect = r.headers.get('X-CDT-Redirect');
            if (redirect) {
                window.location.href = redirect;
                return;
            }
            const json = await r.json();
            if (json.errors) this.errors = json.errors;
        } catch (e) {
            this.errors = {general: 'Something went wrong. Please try again.'};
        }
        this.submitting = false;
    },
}));

Alpine.data('inviteAcceptForm', (token) => ({
    submitting: false,
    errors: {},
    async submit() {
        this.submitting = true;
        this.errors = {};
        try {
            const fd = new FormData(this.$el.querySelector('form'));
            const r = await fetch('/invite/' + encodeURIComponent(token), {
                method: 'POST',
                body: fd,
                headers: {'X-Alpine-Request': 'true'},
            });
            const redirect = r.headers.get('X-CDT-Redirect');
            if (redirect) {
                window.location.href = redirect;
                return;
            }
            const json = await r.json();
            if (json.errors) this.errors = json.errors;
        } catch (e) {
            this.errors = {general: 'Something went wrong. Please try again.'};
        }
        this.submitting = false;
    },
}));

Alpine.data('inviteConfirmForm', (token) => ({
    submitting: false,
    action: null,
    async respond(action) {
        this.submitting = true;
        this.action = action;
        try {
            const fd = new FormData();
            fd.append('action', action);
            const r = await fetch('/invite/' + encodeURIComponent(token) + '/respond', {
                method: 'POST',
                body: fd,
                headers: {'X-Alpine-Request': 'true'},
            });

            const redirect = r.headers.get('X-CDT-Redirect');
            if (redirect) {
                window.location.href = redirect;
                return;
            }

            const json = await r.json().catch(() => null);
            if (json?.redirect) {
                window.location.href = json.redirect;
                return;
            }
            if (json?.errors?.general) {
                Alpine.store('toast').show(json.errors.general, 'error');
                return;
            }
            if (!r.ok) {
                Alpine.store('toast').show('Something went wrong. Please try again.', 'error');
            }
        } catch (e) {
            Alpine.store('toast').show('Something went wrong. Please try again.', 'error');
        }
        this.submitting = false;
        this.action = null;
    },
}));

Alpine.store('global', {})

// remove loginAs from the url after the page loads so it does not stay in browser history
if (new URLSearchParams(window.location.search).has('loginAs')) {
    const url = new URL(window.location.href);
    url.searchParams.delete('loginAs');
    history.replaceState(null, '', url.pathname + (url.search || ''));
}

Alpine.store('sessions', {
    items: [],
    initialized: false,
    rfbInstances: {},  // Store RFB instances by sessionID

    init(serverSessions) {
        serverSessions = serverSessions || [];

        // filter out session that was stopped from /test page
        const stoppingSessionID = Alpine.store('global').stoppingSessionID;
        if (stoppingSessionID) {
            serverSessions = serverSessions.filter(s => s.sessionID !== stoppingSessionID);
            delete Alpine.store('global').stoppingSessionID;
        }

        if (!this.initialized) {
            // First load - use server data
            this.items = serverSessions;
            this.initialized = true;
        } else {
            // Subsequent loads (ajax navigation) - merge sessions
            // Add new sessions that don't exist locally
            for (const serverSession of serverSessions) {
                const exists = this.items.find(s => s.sessionID === serverSession.sessionID);
                if (!exists) {
                    this.items.unshift(serverSession);
                }
            }
            // Remove sessions that no longer exist on server
            this.items = this.items.filter(localSession =>
                serverSessions.find(s => s.sessionID === localSession.sessionID)
            );
            // Sort by startedAt descending (newest first)
            this.items.sort((a, b) => b.startedAt - a.startedAt);
        }
    },

    add(session) {
        this.items.unshift(session);
    },

    remove(sessionID) {
        // Cleanup RFB instance
        if (this.rfbInstances[sessionID]) {
            this.rfbInstances[sessionID].disconnect();
            delete this.rfbInstances[sessionID];
        }
        this.items = this.items.filter(s => s.sessionID !== sessionID);
    },

    setRfb(sessionID, rfb) {
        this.rfbInstances[sessionID] = rfb;
    },

    getRfb(sessionID) {
        return this.rfbInstances[sessionID];
    }
})

Alpine.store('toast', {
    message: '',
    type: 'success',
    visible: false,
    timeout: null,

    show(message, type = 'success', duration = 5000) {
        if (this.timeout) clearTimeout(this.timeout);
        this.message = message;
        this.type = type;
        this.visible = true;
        this.timeout = setTimeout(() => {
            this.visible = false;
        }, duration);
    },

    hide() {
        this.visible = false;
        if (this.timeout) clearTimeout(this.timeout);
    }
})

Alpine.store('confirmModal', {
    title: '',
    message: '',
    resolve: null,
    loading: false,
    keepOpenOnConfirm: false,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    confirmClass: 'btn-primary',

    open(title, message, options = {}) {
        this.title = title;
        this.message = message;
        this.loading = false;
        this.keepOpenOnConfirm = options.keepOpenOnConfirm || false;
        this.confirmText = options.confirmText || 'Confirm';
        this.cancelText = options.cancelText || 'Cancel';
        this.confirmClass = options.confirmClass || 'btn-primary';

        return new Promise((resolve) => {
            this.resolve = resolve;
            document.getElementById('confirm-modal').showModal();
        });
    },

    confirm() {
        if (this.loading) return;

        if (this.keepOpenOnConfirm) {
            this.loading = true;
        } else {
            document.getElementById('confirm-modal').close();
        }

        if (this.resolve) this.resolve(true);
    },

    cancel() {
        if (this.loading) return;

        document.getElementById('confirm-modal').close();
        if (this.resolve) this.resolve(false);
    }
})

Alpine.start()

// apply system theme initially if set to system
if (document.documentElement.dataset.theme === 'system') {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    document.documentElement.dataset.theme = prefersDark ? 'business' : 'corporate';
}

// listen for system theme changes only if theme is system
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (document.documentElement.dataset.theme === 'system') {
        document.documentElement.dataset.theme = e.matches ? 'business' : 'corporate';
    }
});

document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href]')
    if (!link) return

    if (link.hasAttribute('x-target')) return
    if (link.hasAttribute('data-no-ajax')) return
    if (link.target === '_blank') return
    if (link.href.startsWith('mailto:')) return
    if (link.href.startsWith('tel:')) return
    if (link.href.startsWith('javascript:')) return
    if (new URL(link.href).origin !== window.location.origin) return

    link.setAttribute('x-target', 'main')
    link.setAttribute('x-target.push', 'main')
}, true)

// clear stale field errors when a form submits, so the response only re-adds
// the errors that still apply. the matching <div x-sync id="{name}-error"> that
// the server returns will overwrite these cleared ones; fields that now pass
// validation stay clean because the server sends nothing for them.
document.addEventListener('ajax:before', (event) => {
    const form = event.target?.closest?.('form') || (event.target?.tagName === 'FORM' ? event.target : null);
    if (!form) return;
    form.querySelectorAll('.error-message').forEach(el => {
        el.textContent = '';
        el.classList.add('hidden');
    });
});

const redirectFromAjax = (event) => {
    const response = event.detail?.response || event.detail;
    const headerUrl = response?.headers?.get?.('X-CDT-Redirect');
    if (!headerUrl) return false;

    event.preventDefault();
    window.location.href = new URL(headerUrl, window.location.origin).href;
    return true;
};

// Auth boundary changes (expired session/login success) must be full-page
// loads, otherwise the SPA can merge logged-out content into logged-in layout
// or logged-in content into logged-out layout.
document.addEventListener('ajax:redirect', redirectFromAjax);
document.addEventListener('ajax:error', redirectFromAjax);

document.addEventListener('ajax:after', (event) => {
    if (redirectFromAjax(event)) return;

    document.querySelectorAll('[aria-busy]').forEach(el => {
        el.removeAttribute('aria-busy');
    });
});

window.addEventListener('ajax:merged', (event) => {
    if (event.target?.id !== 'main') return;

    const pageTitleEl = event.target.querySelector('[data-page-title]');
    if (!pageTitleEl?.dataset?.pageTitle) return;

    document.title = pageTitleEl.dataset.pageTitle;
});
